/**
 * Pure helpers for shaping `LeadFollowUp[]` into the GHL inbound-webhook payload.
 *
 * v1.0 gate item 8: "write detected customer events into the JSON lead
 * submission payload" (`docs/v1.0-remaining-temp.md`).
 *
 * The original v0 send path only posted contact data in the main inbound
 * webhook and fired separate `bb_action: "create_followup_task"` POSTs per
 * follow-up via {@link ./ghlSenderFollowUps.postGhlOpenFollowUps}. That
 * keeps working unchanged. This module additionally embeds the **same**
 * open follow-ups into the main payload so a GHL workflow can branch on
 * them *before* the individual task POSTs land, enabling time-sensitive
 * automation (e.g. "if first due_date is within 7 days, route to manager
 * follow-up workflow").
 */

import type { LeadFollowUp } from "@/types/lead";

/** Shape sent inside `bb_follow_ups: [...]` — intentionally narrower than `LeadFollowUp`. */
export type GhlPayloadFollowUp = {
  next_action: string;
  due_date: string;
  reason: string;
};

/**
 * Return open follow-ups (status === "open") in the payload's narrow shape.
 * Done/cancelled items are dropped — they would only confuse GHL workflow
 * branches that key off `bb_open_followup_count`. Order is preserved from
 * the input so the GHL side can rely on app-defined ordering.
 */
export function extractOpenFollowUpsForPayload(
  followUps: LeadFollowUp[] | null | undefined,
): GhlPayloadFollowUp[] {
  if (!Array.isArray(followUps)) return [];
  const out: GhlPayloadFollowUp[] = [];
  for (const f of followUps) {
    if (!f || f.status !== "open") continue;
    const next_action = typeof f.next_action === "string" ? f.next_action.trim() : "";
    const due_date = typeof f.due_date === "string" ? f.due_date.trim() : "";
    if (!next_action || !due_date) continue;
    out.push({
      next_action,
      due_date,
      reason: typeof f.reason === "string" ? f.reason.trim() : "",
    });
  }
  return out;
}

/**
 * Earliest `due_date` among the supplied open follow-ups, or `null` when none.
 *
 * Compares as ISO-style strings (YYYY-MM-DD). Calendars in the app
 * normalize due dates to that shape, so lexical comparison is equivalent
 * to chronological comparison without a Date roundtrip.
 */
export function nextOpenFollowUpDueDate(
  followUps: ReadonlyArray<GhlPayloadFollowUp>,
): string | null {
  let best: string | null = null;
  for (const f of followUps) {
    if (!f.due_date) continue;
    if (best === null || f.due_date < best) best = f.due_date;
  }
  return best;
}
